// @flow
import React, { PureComponent } from "react";
import { Message, Select } from "semantic-ui-react";
import { formValues } from "redux-form";
import { connect } from "react-redux";
import i18n from "../../../../i18n";

import { fetchProviders as getProviders, resetProviders } from "../../../../redux/actions/providers";
import { isNumberWithoutDots } from "../../../../Commons/Utilities/Number";

import { PROVIDER_EXTERNAL_ID_IS_NOT_IN_LIST } from "../../../../Commons/Modules/Provider/misc/Constants";

import type { ProviderT } from "Commons/Modules/Provider/misc/Types";
import GoogleTagManager from "../../../../Commons/Utilities/GoogleTagManager";
import { genericGtmEvent } from "Commons/Utilities/GoogleTagManager";
import { mapProvidersShortOffer } from "Commons/Components/FormComponents/misc/Mappers";

type Props = {
  locationId: ?string,
  postalCode: ?string,
  providerId: ?string,
  consumptionGas: ?string,
  consumptionElectricity: ?string,
  placeholder: ?string,
  name: string,
  categoryType: string,
  getProviders: Function,
  resetProviders: Function,
  change: Function,
  value: number,
  loading: ?boolean,
  providers: ProviderT[],
  basicProviders: ProviderT[],
  basicProvider: ProviderT,
  meta: {
    touched: boolean,
    error: ?string,
  },
  isCompany: boolean,
  gtm?: ?Object,
};

const NOT_IN_LIST_OPTION = {
  value: PROVIDER_EXTERNAL_ID_IS_NOT_IN_LIST,
  text: i18n.t("common:notInList"),
};

let DynamicSelectProvider = class DynamicSelectProvider extends PureComponent<Props> {
  componentDidMount() {
    let consumption = this.props.consumptionElectricity ? this.props.consumptionElectricity : this.props.consumptionGas;

    if (
      this.props.postalCode &&
      this.props.postalCode.length === 5 &&
      this.props.locationId !== undefined &&
      this.props.categoryType &&
      consumption
    ) {
      this.props.resetProviders();
      this.props.getProviders(
        this.props.postalCode,
        this.props.categoryType,
        this.props.locationId,
        consumption,
        this.props.isCompany
      );
    }

    if ((!this.props.postalCode || this.props.postalCode.length !== 5) && this.props.providers.length !== 0) {
      this.props.resetProviders();
    }
  }

  componentDidUpdate(prevProps: Props) {
    let consumption = this.props.consumptionElectricity ? this.props.consumptionElectricity : this.props.consumptionGas;
    if (prevProps !== this.props) {
      if (
        this.props.postalCode &&
        this.props.postalCode.length === 5 &&
        this.props.locationId !== undefined &&
        this.props.categoryType &&
        isNumberWithoutDots(consumption)
      ) {
        if (
          this.props.locationId !== prevProps.locationId ||
          (consumption !== prevProps.consumptionElectricity && consumption !== prevProps.consumptionGas)
        ) {
          this.props.getProviders(
            this.props.postalCode,
            this.props.categoryType,
            this.props.locationId,
            consumption,
            this.props.isCompany
          );
        }
      }
    }
    if ((!this.props.postalCode || this.props.postalCode.length !== 5) && this.props.providers.length !== 0) {
      this.props.resetProviders();
    }

    // Calling change from redux form updates the store,
    // which will trigger another render, we need to check
    // if values are different to avoid an infinite loop
    const { basicProvider, value } = this.props;

    if (basicProvider) {
      if (
        value !== basicProvider.company.externalId &&
        value === "" &&
        this.props.postalCode === prevProps.postalCode
      ) {
        this.props.change("companyId", basicProvider.company.externalId);
        this.props.change("companyName", basicProvider.company.companyName);
        this.props.change("tariffs", basicProvider.tariffs.product);
        this.props.change("categoryId", basicProvider.category.id);
        this.props.change("category", basicProvider.category.categoryType);
        this.props.change("categoryProviderId", basicProvider.id);
      }
    }
  }

  render() {
    let {
      value,
      meta: { error, touched },
      providers,
      basicProviders,
      basicProvider,
      loading,
    } = this.props;

    let options = [...basicProviders, ...providers];

    if (basicProvider) {
      options.unshift({
        ...basicProvider,
        company: {
          ...basicProvider.company,
          externalId: NOT_IN_LIST_OPTION.value,
          companyName: NOT_IN_LIST_OPTION.text,
        },
      });
    }

    return (
      <>
        <Select
          lazyLoad
          fluid
          search
          icon="search"
          loading={loading}
          value={value}
          disabled={options.length === 0}
          options={options.map(mapProvidersShortOffer)}
          error={!!error && touched}
          noResultsMessage={i18n.t("common:notResultsFound")}
          onChange={(_, { value }) => {
            const payload = this.props.gtm?.payload ? { clickedStep: this.props.gtm?.payload } : null;
            this.props.gtm && genericGtmEvent(this.props.gtm.event, payload);
            GoogleTagManager.providerListClicked();
            let chosenOption = options.find(({ company }) => company.externalId === value);

            if (chosenOption) {
              if (value === NOT_IN_LIST_OPTION.value) {
                this.props.change("companyId", basicProvider.company.externalId);
                this.props.change("companyName", basicProvider.company.companyName);
              } else {
                this.props.change("companyId", value);
                this.props.change("companyName", chosenOption.company.companyName);
              }

              this.props.change("tariffs", chosenOption.tariffs.product);
              this.props.change("categoryId", providers[0].category.id);
              this.props.change("category", providers[0].category.categoryType);
              this.props.change("categoryProviderId", chosenOption.id);
            }
          }}
          data-cy="provider-select"
        />
        {!!error && touched && <Message negative content={error} />}
      </>
    );
  }
};

DynamicSelectProvider = formValues({
  postalCode: "postalcode",
  locationId: "locationId",
  providerId: "companyId",
  consumptionElectricity: "electricityYearlyConsumption",
  consumptionGas: "gasYearlyConsumption",
})(DynamicSelectProvider);

const mapStateToProps = ({ providers }, props) => {
  const basicProviders = (providers.providers[props.categoryType] || [])
    .filter((provider) => provider.isBasicProvider)
    .map((provider) => ({
      ...provider,
      company: {
        ...provider.company,
        companyName: `${provider.company.companyName} (Standardanbieter)`,
      },
    }));

  return {
    basicProviders,
    basicProvider: basicProviders[0],
    providers: (providers.providers[props.categoryType] || []).filter((provider) => !provider.isBasicProvider),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getProviders: (category, postalCode, locationId, consumption, isCompany) =>
    dispatch(getProviders(category, postalCode, locationId, consumption, isCompany)),
  resetProviders: () => dispatch(resetProviders()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DynamicSelectProvider);
